// The windows behind a stock's ★ and ⊘: choose which watchlists a stock is in (or make a new one),
// and blacklist it with a reason. Also the quick menu a long-press or right-click on any stock code
// opens. Each change refreshes the marks drawn on every code (format.ts) and calls `changed`.
import { api } from "./api";
import { codePill, esc, getMarks, setMarks, type Marks } from "./format";

interface WatchLists { lists: { id: number; name: string; color: string; count: number }[]; stocks: { code: string; note: string | null; lists: number[] }[] }

export async function refreshMarks() {
  setMarks(await api<Marks>("marks"));
}

/** A modal window (a bottom sheet on phones). Closes on Escape, a tap outside, or the ✕. */
function modal(title: string, body: string) {
  document.querySelector("dialog.sheet")?.remove();
  document.body.insertAdjacentHTML("beforeend", `<dialog class="sheet" aria-label="${esc(title)}">
    <header><h3>${title}</h3><button class="ghost icon close" aria-label="Close">✕</button></header>
    <div class="sheet-body">${body}</div></dialog>`);
  const d = document.querySelector<HTMLDialogElement>("dialog.sheet")!;
  d.querySelector(".close")!.addEventListener("click", () => d.close());
  d.addEventListener("click", (e) => { if (e.target === d) d.close(); }); // the backdrop
  d.addEventListener("close", () => d.remove());
  d.showModal();
  return d;
}

export async function openWatch(code: string, changed: () => void) {
  const d = modal(`Watch ${codePill(code)}`, `<div class="loading" role="status"><span></span>Loading…</div>`);
  const body = d.querySelector<HTMLElement>(".sheet-body")!;
  let w: WatchLists;
  try { w = await api<WatchLists>("watchlists"); } catch (e) { body.innerHTML = `<p class="notice error">${esc((e as Error).message)}</p>`; return; }
  const mine = w.stocks.find((s) => s.code === code);
  const chosen = new Set(mine?.lists ?? []);
  let watching = !!mine;
  const draw = () => {
    body.innerHTML = `
      <label class="row-check strong"><input type="checkbox" id="w-on"${watching ? " checked" : ""}> Watch ${esc(code)}</label>
      <h4>Watchlists</h4>
      ${w.lists.length ? `<ul class="plain list-picks">${w.lists.map((l) => `<li><label class="row-check"><input type="checkbox" data-list="${l.id}"${chosen.has(l.id) ? " checked" : ""}>
        <i class="key ${esc(l.color)}"></i> ${esc(l.name)} <span class="muted small">${l.count}</span></label></li>`).join("")}</ul>` : `<p class="muted small">No watchlists yet. Make one below.</p>`}
      <form id="w-new" class="form-row"><input name="name" maxlength="40" placeholder="New watchlist" aria-label="New watchlist name"><button class="ghost">Add</button></form>
      <label class="stack">Note<textarea id="w-note" rows="2" maxlength="500" placeholder="Why you're watching it">${esc(mine?.note ?? "")}</textarea></label>
      <div class="sheet-actions"><button class="primary" id="w-save">Save</button></div>
      <p class="small muted" id="w-msg"></p>`;
    body.querySelector<HTMLInputElement>("#w-on")!.addEventListener("change", (e) => { watching = (e.target as HTMLInputElement).checked; if (!watching) chosen.clear(); draw(); });
    body.querySelectorAll<HTMLInputElement>("[data-list]").forEach((c) => c.addEventListener("change", () => {
      const id = Number(c.dataset.list);
      if (c.checked) { chosen.add(id); watching = true; } else chosen.delete(id);
      body.querySelector<HTMLInputElement>("#w-on")!.checked = watching;
    }));
    body.querySelector<HTMLFormElement>("#w-new")!.addEventListener("submit", async (e) => {
      e.preventDefault();
      const name = String(new FormData(e.target as HTMLFormElement).get("name") ?? "").trim();
      if (!name) return;
      const note = body.querySelector<HTMLTextAreaElement>("#w-note")!.value;
      try {
        w = await api<WatchLists>("watchlists", "POST", { name });
        const made = w.lists.find((l) => l.name.toLowerCase() === name.toLowerCase());
        if (made) { chosen.add(made.id); watching = true; }
        draw();
        body.querySelector<HTMLTextAreaElement>("#w-note")!.value = note;
      } catch (x) { body.querySelector("#w-msg")!.textContent = (x as Error).message; }
    });
    body.querySelector("#w-save")!.addEventListener("click", async () => {
      const msg = body.querySelector("#w-msg")!;
      try {
        if (!watching) { if (mine) await api(`watchlist/${code}`, "DELETE"); }
        else {
          if (!mine) await api(`watchlist/${code}`, "PUT");
          await api(`watchlist/${code}`, "PATCH", { lists: [...chosen], note: body.querySelector<HTMLTextAreaElement>("#w-note")!.value });
        }
        await refreshMarks();
        d.close();
        changed();
      } catch (x) { msg.textContent = (x as Error).message; }
    });
  };
  draw();
}

export function openBlacklist(code: string, changed: () => void) {
  const b = getMarks().blacklisted[code];
  const d = modal(b ? `${codePill(code)} is blacklisted` : `Blacklist ${codePill(code)}`, b ? `
      <p>${b.reason ? `Reason: <b>${esc(b.reason)}</b>` : "No reason given."}</p>
      <p class="small muted">Blacklisted ${new Date(b.addedAt).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })}. It still shows everywhere, marked ⊘ so you remember.</p>
      <label class="stack">Reason<textarea id="b-reason" rows="2" maxlength="300">${esc(b.reason ?? "")}</textarea></label>
      <div class="sheet-actions"><button class="ghost" id="b-save">Update reason</button><button class="primary" id="b-off">Remove from blacklist</button></div>
      <p class="small muted" id="b-msg"></p>` : `
      <p class="small">A reminder that you've ruled this stock out. It still shows everywhere, greyed with ⊘, and the reason shows on its page.</p>
      <label class="stack">Why? <textarea id="b-reason" rows="3" maxlength="300" placeholder="e.g. management keeps missing targets"></textarea></label>
      <div class="sheet-actions"><button class="primary danger" id="b-save">Blacklist ${esc(code)}</button></div>
      <p class="small muted" id="b-msg"></p>`);
  const msg = d.querySelector("#b-msg")!;
  const done = async (p: Promise<unknown>) => {
    try { setMarks(await (p as Promise<Marks>)); d.close(); changed(); } catch (x) { msg.textContent = (x as Error).message; }
  };
  d.querySelector("#b-save")!.addEventListener("click", () => done(api<Marks>(`blacklist/${code}`, "PUT", { reason: d.querySelector<HTMLTextAreaElement>("#b-reason")!.value })));
  d.querySelector("#b-off")?.addEventListener("click", () => done(api<Marks>(`blacklist/${code}`, "DELETE")));
  (d.querySelector("#b-reason") as HTMLTextAreaElement).focus();
}

/** The long-press / right-click menu on any stock code. */
export function openQuickMenu(code: string, changed: () => void) {
  const m = getMarks();
  const held = m.held.includes(code), watched = m.watched.includes(code), black = m.blacklisted[code];
  const d = modal(codePill(code), `
    <p class="small muted">${[held && "In your portfolio", watched && `On your watchlist${m.lists[code]?.length ? ` (${esc(m.lists[code].join(", "))})` : ""}`, black && `Blacklisted${black.reason ? `: ${esc(black.reason)}` : ""}`].filter(Boolean).join(" · ") || "Not held or watched."}</p>
    <div class="menu-list">
      <a class="menu-item" href="#/${esc(code)}">Open ${esc(code)}</a>
      <button class="menu-item" id="q-watch">★ ${watched ? "Watchlists…" : "Watch…"}</button>
      <button class="menu-item" id="q-black">⊘ ${black ? "Blacklisted: view or remove" : "Blacklist…"}</button>
    </div>`);
  d.querySelector("a.menu-item")!.addEventListener("click", () => d.close());
  d.querySelector("#q-watch")!.addEventListener("click", () => { d.close(); openWatch(code, changed); });
  d.querySelector("#q-black")!.addEventListener("click", () => { d.close(); openBlacklist(code, changed); });
}

/**
 * Long-press (touch, 500 ms) or right-click on anything with data-code opens the quick menu.
 * The click that follows a long-press is swallowed so the link doesn't also open.
 */
export function installQuickMenu(enabled: () => boolean, changed: () => void) {
  let timer = 0, fired = false, startX = 0, startY = 0;
  const codeAt = (t: EventTarget | null) => (t as HTMLElement | null)?.closest?.<HTMLElement>("[data-code]")?.dataset.code;
  document.addEventListener("contextmenu", (e) => {
    const code = codeAt(e.target);
    if (!code || !enabled()) return;
    e.preventDefault();
    if (!fired) openQuickMenu(code, changed);
  });
  document.addEventListener("touchstart", (e) => {
    const code = codeAt(e.target);
    fired = false;
    if (!code || !enabled()) return;
    startX = e.touches[0].clientX; startY = e.touches[0].clientY;
    timer = window.setTimeout(() => { fired = true; navigator.vibrate?.(10); openQuickMenu(code, changed); }, 500);
  }, { passive: true });
  const cancel = () => clearTimeout(timer);
  document.addEventListener("touchmove", (e) => { if (Math.hypot(e.touches[0].clientX - startX, e.touches[0].clientY - startY) > 10) cancel(); }, { passive: true });
  document.addEventListener("touchend", cancel);
  document.addEventListener("click", (e) => { if (fired && codeAt(e.target)) { e.preventDefault(); e.stopPropagation(); fired = false; } }, true);
}
